import { useCallback, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Restore as RestoreIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import type { Entity } from '@/types/Entity';
import { httpClient, isApiError } from '@/utils/httpClient';
import { useNotifications } from '@/stores/useNotificationStore';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';

interface ReactivateIeKitDialogProps {
  open: boolean;
  kit: Entity | null | undefined;
  onClose: () => void;
}

interface ReactivateResult {
  kit_items_reactivated: number;
}

export const ReactivateIeKitDialog = ({ open, kit, onClose }: ReactivateIeKitDialogProps) => {
  const queryClient = useQueryClient();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const isViewer = useWriteGuard();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!kit) return;

    setIsSubmitting(true);
    try {
      const response = await httpClient.post<ReactivateResult[]>(
        '/api/rest/data/v1/rpc/reactivate_ie_kit',
        { p_ie_kit_id: kit.id },
      );

      const result = Array.isArray(response) ? response[0] : response;

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });

      const detail = result?.kit_items_reactivated
        ? ` (${result.kit_items_reactivated} kit item(s) restored)`
        : '';

      notifySuccess(
        `${ieKitLabel} Reactivated`,
        `"${kit.display_name}" has been reactivated.${detail}`,
      );
      onClose();
    } catch (err: unknown) {
      const message =
        isApiError(err) || err instanceof Error
          ? err.message
          : `Failed to reactivate ${ieKitLabel}.`;
      notifyError('Reactivate Failed', message);
    } finally {
      setIsSubmitting(false);
    }
  }, [kit, queryClient, notifySuccess, notifyError, onClose]);

  const kitName = kit?.display_name ?? kit?.entity_identifier ?? `this ${ieKitLabel.toLowerCase()}`;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isSubmitting) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose();
      }}
      disableEscapeKeyDown={isSubmitting}
      maxWidth="xs"
      aria-labelledby="reactivate-ie-kit-title"
      aria-describedby="reactivate-ie-kit-description"
    >
      <DialogTitle
        id="reactivate-ie-kit-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <RestoreIcon color="info" />
        Reactivate {ieKitLabel}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="reactivate-ie-kit-description">
          Are you sure you want to reactivate <strong>{kitName}</strong>? This will restore the kit
          and its items to active status. Person assignments and hand receipt references that were
          removed during inactivation will need to be re-established manually.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          onClick={() => void handleConfirm()}
          color="primary"
          variant="contained"
          disabled={isSubmitting || isViewer}
        >
          {isSubmitting ? 'Reactivating...' : 'Yes, Reactivate'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
